// Ported from JurisdictionMapOverlay.jsx on 2026-10-12.
import MapToolsPanel from 'components/BaseMap/components/MapToolsPanel/MapToolsPanel';
import type { PathOptions } from 'leaflet';
import type { JurisdictionMapOverlayProps } from './types/jurisdictionMapOverlay.types';
import { Polygon } from 'react-leaflet';
import './jurisdictionMapOverlay.scss';

// CSS owns jurisdiction colors so existing paths respond immediately to a color-mode change.
// Offered and selected FEMA areas share a color; their weights, dashes, and fills identify state.
export const FEMA_IPAWS_OFFERED_JURISDICTION_PATH_OPTIONS: PathOptions = {
  className: 'jurisdiction-map-path is-fema-ipaws is-area',
  color: 'var(--color-map-jurisdiction-fema)',
  fillColor: 'var(--color-map-jurisdiction-fema)',
  weight: 1,
  dashArray: '4 6',
  opacity: 0.8,
  fillOpacity: 0.12,
};
export const FEMA_IPAWS_SELECTED_JURISDICTION_PATH_OPTIONS: PathOptions = {
  className: 'jurisdiction-map-path is-fema-ipaws is-area',
  color: 'var(--color-map-jurisdiction-fema)',
  fillColor: 'var(--color-map-jurisdiction-fema)',
  weight: 2,
  // Leaflet merges style updates; explicitly clear the previously offered area's dashes.
  dashArray: undefined,
  opacity: 1,
  fillOpacity: 0.18,
};

const JURISDICTION_PATH_OPTIONS: Record<'rescAlert' | 'femaIpaws', PathOptions> = {
  rescAlert: {
    className: 'jurisdiction-map-path is-rescalert',
    color: 'var(--color-map-jurisdiction-rescalert)',
    fill: false,
    opacity: 1,
    weight: 3,
  },
  femaIpaws: {
    className: 'jurisdiction-map-path is-fema-ipaws',
    color: 'var(--color-map-jurisdiction-fema)',
    fill: false,
    opacity: 1,
    weight: 2,
  },
};

/** Draws alerting-authority boundaries and explains the visible line colors. */
const JurisdictionMapOverlay = ({
  workplacePolygons = [],
  femaBoundaryRings = [],
  hasOfferedFemaAreas = false,
}: JurisdictionMapOverlayProps) => {
  const hasWorkplaceBoundary = workplacePolygons.length > 0;
  const hasFemaBoundary = femaBoundaryRings.length > 0 || hasOfferedFemaAreas;

  if (!hasWorkplaceBoundary && !hasFemaBoundary) return null;

  return (
    <>
      {workplacePolygons.map((polygon, index) => (
        <Polygon
          key={`rescalert-${JURISDICTION_PATH_OPTIONS.rescAlert.className}-${index}`}
          positions={polygon}
          className={JURISDICTION_PATH_OPTIONS.rescAlert.className}
          pathOptions={JURISDICTION_PATH_OPTIONS.rescAlert}
          interactive={false}
        />
      ))}
      {femaBoundaryRings.map((ring, index) => (
        <Polygon
          key={`fema-${JURISDICTION_PATH_OPTIONS.femaIpaws.className}-${index}`}
          positions={ring}
          className={JURISDICTION_PATH_OPTIONS.femaIpaws.className}
          pathOptions={JURISDICTION_PATH_OPTIONS.femaIpaws}
          interactive={false}
        />
      ))}

      <MapToolsPanel position="bottomleft" width={310} header="Map jurisdiction legend" collapsible>
        <ul className="jurisdiction-map-legend list-unstyled mb-0" aria-label="Jurisdiction lines">
          {hasWorkplaceBoundary ? (
            <li>
              <span className="jurisdiction-map-legend-line is-rescalert" aria-hidden="true" />
              <span>RescAlert Jurisdiction (Alerting Authority)</span>
            </li>
          ) : null}
          {hasFemaBoundary ? (
            <li>
              <span className="jurisdiction-map-legend-line is-fema-ipaws" aria-hidden="true" />
              <span>FEMA IPAWS Jurisdiction (Alerting Authority)</span>
            </li>
          ) : null}
        </ul>
      </MapToolsPanel>
    </>
  );
};

export default JurisdictionMapOverlay;
